export interface ScatterPoint {
    x: number;
    y: number;
    color: string;
    label?: string;
}

interface ScatterOptions {
    points: ScatterPoint[];
    showGrid: boolean;
    xLabel: string;
    yLabel: string;
}

const W = 520;
const H = 440;
const ML = 64;
const MR = 20;
const MT = 42;
const MB = 52;

export default function drawScatter(canvas: HTMLCanvasElement, opts: ScatterOptions): void {
    canvas.width = W;
    canvas.height = H;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { points, showGrid, xLabel, yLabel } = opts;
    const plotW = W - ML - MR;
    const plotH = H - MT - MB;

    ctx.clearRect(0, 0, W, H);

    const { axXMin, axXMax, xTick, axYMin, axYMax, yTick } = computeBounds(points);

    const toX = (v: number) => ML + ((v - axXMin) / (axXMax - axXMin)) * plotW;
    const toY = (v: number) => MT + plotH - ((v - axYMin) / (axYMax - axYMin)) * plotH;

    const zeroX = toX(0);
    const zeroY = toY(0);

    // Clamp axis lines to plot boundaries when 0 is outside the data range
    const axisX = Math.max(ML, Math.min(ML + plotW, zeroX));
    const axisY = Math.max(MT, Math.min(MT + plotH, zeroY));
    const zeroInRangeX = axXMin <= 0 && 0 <= axXMax;
    const zeroInRangeY = axYMin <= 0 && 0 <= axYMax;

    // Grid lines
    if (showGrid) {
        const xMinor = minorStep(xTick);
        const yMinor = minorStep(yTick);

        // Minor grid lines
        ctx.strokeStyle = '#e2e2dc';
        ctx.lineWidth = 0.75;
        const xMinorCount = Math.round((axXMax - axXMin) / xMinor) + 1;
        for (let i = 0; i < xMinorCount; i++) {
            const px = toX(axXMin + i * xMinor);
            ctx.beginPath();
            ctx.moveTo(px, MT);
            ctx.lineTo(px, MT + plotH);
            ctx.stroke();
        }
        const yMinorCount = Math.round((axYMax - axYMin) / yMinor) + 1;
        for (let i = 0; i < yMinorCount; i++) {
            const py = toY(axYMin + i * yMinor);
            ctx.beginPath();
            ctx.moveTo(ML, py);
            ctx.lineTo(ML + plotW, py);
            ctx.stroke();
        }

        // Major grid lines (at tick label positions)
        ctx.strokeStyle = '#cecec8';
        ctx.lineWidth = 1;
        const xCount = Math.round((axXMax - axXMin) / xTick) + 1;
        for (let i = 0; i < xCount; i++) {
            const px = toX(axXMin + i * xTick);
            ctx.beginPath();
            ctx.moveTo(px, MT);
            ctx.lineTo(px, MT + plotH);
            ctx.stroke();
        }
        const yCount = Math.round((axYMax - axYMin) / yTick) + 1;
        for (let i = 0; i < yCount; i++) {
            const py = toY(axYMin + i * yTick);
            ctx.beginPath();
            ctx.moveTo(ML, py);
            ctx.lineTo(ML + plotW, py);
            ctx.stroke();
        }
    }

    // Axes
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(ML, axisY);
    ctx.lineTo(ML + plotW, axisY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(axisX, MT);
    ctx.lineTo(axisX, MT + plotH);
    ctx.stroke();

    // Arrowheads
    arrowH(ctx, ML + plotW, axisY, '#9ca3af', 6);
    arrowV(ctx, axisX, MT, '#9ca3af', 6);

    // Axis name labels
    ctx.font = '500 13px system-ui, sans-serif';
    ctx.fillStyle = '#374151';

    // X label: centred below the plot area
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(xLabel || 'x', ML + plotW / 2, H - 6);

    // Y label: rotated, centred beside the plot area
    ctx.save();
    ctx.translate(13, MT + plotH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(yLabel || 'y', 0, 0);
    ctx.restore();

    // Tick marks and labels
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillStyle = '#6b7280';
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1;

    const xCount = Math.round((axXMax - axXMin) / xTick) + 1;
    for (let i = 0; i < xCount; i++) {
        const v = axXMin + i * xTick;
        if (zeroInRangeX && Math.abs(v) < xTick * 0.001) continue;
        const px = toX(v);
        ctx.beginPath();
        ctx.moveTo(px, axisY - 4);
        ctx.lineTo(px, axisY + 4);
        ctx.stroke();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#6b7280';
        ctx.fillText(fmt(v), px, axisY + 7);
    }

    const yCount = Math.round((axYMax - axYMin) / yTick) + 1;
    for (let i = 0; i < yCount; i++) {
        const v = axYMin + i * yTick;
        if (zeroInRangeY && Math.abs(v) < yTick * 0.001) continue;
        const py = toY(v);
        ctx.beginPath();
        ctx.moveTo(axisX - 4, py);
        ctx.lineTo(axisX + 4, py);
        ctx.stroke();
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#6b7280';
        ctx.fillText(fmt(v), axisX - 9, py);
    }

    // Origin label — only when 0 is within both axes' visible range
    if (zeroInRangeX && zeroInRangeY) {
        ctx.font = '11px system-ui, sans-serif';
        ctx.fillStyle = '#9ca3af';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'top';
        ctx.fillText('0', axisX - 5, axisY + 5);
    }

    // Points
    const R = 5;
    points.forEach((pt) => {
        const px = toX(pt.x);
        const py = toY(pt.y);

        ctx.beginPath();
        ctx.arc(px, py, R, 0, Math.PI * 2);
        ctx.fillStyle = pt.color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.18)';
        ctx.lineWidth = 1;
        ctx.stroke();

        const label = pt.label?.trim() || `(${fmt(pt.x)}, ${fmt(pt.y)})`;
        ctx.font = '10px system-ui, sans-serif';
        const tw = ctx.measureText(label).width;

        let lx = px + R + 4;
        let ly = py - R - 3;
        if (lx + tw > ML + plotW) lx = px - R - 4 - tw;
        if (ly < MT) ly = py + R + 12;

        ctx.fillStyle = '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillText(label, lx, ly);
    });

    // Empty state hint
    if (points.length === 0) {
        ctx.font = '13px system-ui, sans-serif';
        ctx.fillStyle = '#c4c4be';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Add points using the controls →', W / 2, H / 2);
    }
}

function computeBounds(points: ScatterPoint[]): {
    axXMin: number;
    axXMax: number;
    xTick: number;
    axYMin: number;
    axYMax: number;
    yTick: number;
} {
    if (points.length === 0) {
        return { axXMin: -5, axXMax: 5, xTick: 1, axYMin: -5, axYMax: 5, yTick: 1 };
    }

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);

    let xMin = Math.min(...xs);
    let xMax = Math.max(...xs);
    let yMin = Math.min(...ys);
    let yMax = Math.max(...ys);

    // Ensure minimum range so a single point isn't pinched
    if (xMax - xMin < 2) {
        xMin -= 1;
        xMax += 1;
    }
    if (yMax - yMin < 2) {
        yMin -= 1;
        yMax += 1;
    }

    // Padding on all extremes
    const xPad = (xMax - xMin) * 0.12;
    const yPad = (yMax - yMin) * 0.12;
    xMin -= xPad;
    xMax += xPad;
    yMin -= yPad;
    yMax += yPad;

    const { niceMin: axXMin, niceMax: axXMax, tickSpacing: xTick } = niceScale(xMin, xMax, 10);
    const { niceMin: axYMin, niceMax: axYMax, tickSpacing: yTick } = niceScale(yMin, yMax, 10);

    return { axXMin, axXMax, xTick, axYMin, axYMax, yTick };
}

function niceScale(
    min: number,
    max: number,
    maxTicks: number,
): { niceMin: number; niceMax: number; tickSpacing: number } {
    const range = max - min || 1;
    const tickSpacing = niceNum(range / (maxTicks - 1), true);
    const niceMin = Math.floor(min / tickSpacing) * tickSpacing;
    const niceMax = Math.ceil(max / tickSpacing) * tickSpacing;
    return { niceMin, niceMax, tickSpacing };
}

function minorStep(major: number): number {
    return niceNum(major / 5, true);
}

function niceNum(v: number, round: boolean): number {
    const exp = Math.floor(Math.log10(v));
    const frac = v / Math.pow(10, exp);
    let nice: number;
    if (round) {
        nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
    } else {
        nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
    }
    return nice * Math.pow(10, exp);
}

function fmt(n: number): string {
    if (Number.isInteger(n)) return n.toString();
    return parseFloat(n.toFixed(2)).toString();
}

function arrowH(
    ctx: CanvasRenderingContext2D,
    tipX: number,
    tipY: number,
    color: string,
    size: number,
): void {
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(tipX - size, tipY - size / 2);
    ctx.lineTo(tipX - size, tipY + size / 2);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
}

function arrowV(
    ctx: CanvasRenderingContext2D,
    tipX: number,
    tipY: number,
    color: string,
    size: number,
): void {
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(tipX - size / 2, tipY + size);
    ctx.lineTo(tipX + size / 2, tipY + size);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
}
